import React, { useCallback } from 'react'

import classNames from 'clsx'
import memoizeOne from 'memoize-one'
import PropTypes from 'prop-types'

import { Delete as DeleteIcon } from '@mui/icons-material'
import {
  Card,
  CardHeader,
  CardMedia,
  IconButton,
} from '@mui/material'
import { withStyles } from '@mui/styles'

import { useFilestackURL } from '~/src/Lib/Utils'
import LoadingAttachment from '~/src/UI/Shared/LoadingAttachment'

const TOOLTIP_HEIGHT = 300 * (9 / 16)

const styles = theme => ({
  root: {
    width: '100%',
    position: 'relative',
    '&$tooltip': {
      padding: theme.spacing(0.75, 0.25),
      width: 300,
      height: TOOLTIP_HEIGHT
    },
    '&$tooltip $media': {
      maxHeight: '100%'
    },
    '&$transparent': {
      backgroundColor: 'transparent',
    }
  },
  tooltip: {},
  transparent: {},
  header: {
    padding: `0 ${theme.spacing()}px`,
    position: 'absolute',
    width: '100%',
    '& button': {
      position: 'absolute',
      right: 0,
      top: 0,
    },
    '& span, & p': {
      textShadow: `1px 1px 2px ${theme.palette.monochrome[4]}, 0 0 0.7em ${theme.palette.blue.darker}`,
    },
    '& [class*=MuiTypography-body2]': {
      color: theme.palette.text.primary,
    }
  },
  headerContent: {
    width: '100%',
  },
  subheader: {
    color: theme.palette.text.primary,
    display: 'block',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
  },
  media: {
    maxWidth: '35vw',
    maxHeight: '50vh',
    margin: '0 auto',
    objectFit: 'contain',
    [theme.breakpoints.down('sm')]: {
      maxWidth: '100%',
      maxHeight: '67vh',
    }
  },
  mediaLink: {
    display: 'contents'
  }
})

const getCardHeaderClasses = memoizeOne(({
  header: root,
  headerContent: content,
  subheader
}) => ({
  root,
  content,
  subheader,
}))

const LinkWrapper = ({ tooltip, ...props }) => (
  // eslint-disable-next-line jsx-a11y/anchor-has-content
  tooltip ? props.children : <a {...props} />
)

LinkWrapper.propTypes = {
  tooltip: PropTypes.bool.isRequired,
  children: PropTypes.node.isRequired,
}

export const ImageCardComponent = ({
  classes,
  description = null,
  square = false,
  title = null,
  tooltip = false,
  transparent = false,
  onDelete = null,
  ...rest
}) => {
  const filestackURL = useFilestackURL()

  // eslint-disable-next-line react/no-unstable-nested-components, react-hooks/exhaustive-deps
  const ImageWrapper = useCallback(React.forwardRef(({ src, ...mediaProps }, ref) => (
    <CardMedia ref={ref} {...mediaProps} component="img" image={src} />
  )), [])

  return (
    <Card
      elevation={0}
      square={square}
      className={classNames({
        [classes.root]: true,
        [classes.tooltip]: tooltip,
        [classes.transparent]: transparent,
      })}
    >
      {title || description ? (
        <CardHeader
          classes={getCardHeaderClasses(classes)}
          action={
            onDelete ? (
              <IconButton
                data-testid="delete-image-attachment-icon"
                onClick={onDelete}
                size="large"
              >
                <DeleteIcon />
              </IconButton>
            ) : null
          }
          title={title}
          subheader={description}
        />
      ) : null}
      <LinkWrapper
        tooltip={tooltip}
        href={filestackURL(rest)}
        target="_blank"
        rel="noopener noreferrer"
        className={classes.mediaLink}
      >
        <LoadingAttachment
          alt={title}
          className={classes.media}
          component={ImageWrapper}
          attachment={rest}
        />
      </LinkWrapper>
    </Card>
  )
}
ImageCardComponent.propTypes = {
  description: PropTypes.string,
  filename: PropTypes.string,
  square: PropTypes.bool,
  title: PropTypes.node,
  tooltip: PropTypes.bool,
  /* eslint-disable react/no-unused-prop-types */
  url: PropTypes.string.isRequired,
  handle: PropTypes.string.isRequired,
  /* eslint-enable react/no-unused-prop-types */
  onDelete: PropTypes.func,
  transparent: PropTypes.bool,
}

export default withStyles(styles, { name: 'Card/Image' })(React.memo(ImageCardComponent))
